"use client";

import { useState, useEffect } from "react";
import { Link } from "@/i18n/routing";
import {
  ArrowLeft,
  RotateCcw,
  ShoppingBag,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { cn, formatPrice } from "@/lib/utils";
import { SAMPLE_WIGS } from "@/lib/sample-data";
import { useTranslations } from "next-intl";
import { trackEvent } from "@/lib/track";

interface Question {
  id: string;
  question: string;
  options: { label: string; value: string }[];
}

const QUESTIONS: Question[] = [
  {
    id: "purpose",
    question: "Why are you looking for a wig?",
    options: [
      { label: "Fashion", value: "fashion" },
      { label: "Medical", value: "medical" },
      { label: "Religious", value: "religious" },
      { label: "Special Occasion", value: "occasion" },
    ],
  },
  {
    id: "texture",
    question: "What's your natural hair texture?",
    options: [
      { label: "Straight", value: "straight" },
      { label: "Wavy", value: "wavy" },
      { label: "Curly", value: "curly" },
      { label: "Coily", value: "coily" },
    ],
  },
  {
    id: "length",
    question: "What length are you going for?",
    options: [
      { label: "Short - Above shoulders", value: "short" },
      { label: "Medium - Shoulder length", value: "medium" },
      { label: "Long - Mid-back", value: "long" },
      { label: "Extra Long", value: "extra-long" },
    ],
  },
  {
    id: "lifestyle",
    question: "How active is your lifestyle?",
    options: [
      { label: "Very Active", value: "very-active" },
      { label: "Moderately Active", value: "moderate" },
      { label: "Low Activity", value: "low" },
    ],
  },
  {
    id: "budget",
    question: "What's your budget range?",
    options: [
      { label: "Under $2,000", value: "under-2k" },
      { label: "$2,000 - $4,000", value: "2k-4k" },
      { label: "$4,000 - $6,000", value: "4k-6k" },
      { label: "$6,000+", value: "6k-plus" },
    ],
  },
  {
    id: "color",
    question: "Any color preference?",
    options: [
      { label: "Dark Tones", value: "dark" },
      { label: "Light Tones", value: "light" },
      { label: "Red / Auburn", value: "red" },
      { label: "Balayage", value: "balayage" },
      { label: "No Preference", value: "none" },
    ],
  },
];

interface QuizResult {
  product: (typeof SAMPLE_WIGS)[0];
  matchPercent: number;
  reason: string;
}

function getResults(
  answers: Record<string, string>
): QuizResult[] {
  // Simple matching logic based on answers
  const scored = SAMPLE_WIGS.filter((w) => w.inStock).map((wig) => {
    let score = 50; // base score

    // Texture match
    const wigTexture = wig.specs.Texture?.toLowerCase() || "";
    if (answers.texture === "straight" && wigTexture === "straight") score += 15;
    if (answers.texture === "wavy" && wigTexture === "wavy") score += 15;
    if (answers.texture === "wavy" && wigTexture === "body wave") score += 12;
    if (answers.texture === "curly" && wigTexture === "curly") score += 15;

    // Length match
    const lengthNum = parseInt(wig.specs.Length || "0");
    if (answers.length === "short" && lengthNum <= 12) score += 12;
    if (answers.length === "medium" && lengthNum >= 14 && lengthNum <= 16) score += 12;
    if (answers.length === "long" && lengthNum >= 18 && lengthNum <= 20) score += 12;
    if (answers.length === "extra-long" && lengthNum >= 22) score += 12;

    // Budget match
    if (answers.budget === "under-2k" && wig.price < 2000) score += 10;
    if (answers.budget === "2k-4k" && wig.price >= 2000 && wig.price <= 4000) score += 10;
    if (answers.budget === "4k-6k" && wig.price >= 4000 && wig.price <= 6000) score += 10;
    if (answers.budget === "6k-plus" && wig.price >= 6000) score += 10;

    // Activity match
    if (answers.lifestyle === "very-active" && wig.specs["Base Type"] === "Mono Top") score += 8;
    if (answers.lifestyle === "low" && wig.specs["Base Type"] === "Lace Top") score += 8;

    // Color match
    const colorNames = wig.colors.map((c) => c.name.toLowerCase()).join(" ");
    if (answers.color === "dark" && (colorNames.includes("dark") || colorNames.includes("jet black"))) score += 8;
    if (answers.color === "light" && (colorNames.includes("light") || colorNames.includes("blonde"))) score += 8;
    if (answers.color === "red" && (colorNames.includes("auburn") || colorNames.includes("chestnut"))) score += 8;

    // Cap to 98
    const capped = Math.min(score, 98);

    return { product: wig, score: capped };
  });

  scored.sort((a, b) => b.score - a.score);
  const top3 = scored.slice(0, 3);

  const matchPercents = [98, 94, 89];
  const reasons = [
    "Perfect match for your texture preference and lifestyle needs",
    "Great fit for your desired length and budget range",
    "Excellent option with versatile styling and quality construction",
  ];

  return top3.map((item, i) => ({
    product: item.product,
    matchPercent: matchPercents[i],
    reason: reasons[i],
  }));
}

export default function QuizPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState<QuizResult[] | null>(null);
  const t = useTranslations("quiz");
  const tc = useTranslations("common");

  useEffect(() => {
    trackEvent("quiz_started");
  }, []);

  const totalSteps = QUESTIONS.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;
  const currentQuestion = QUESTIONS[currentStep];

  function selectAnswer(value: string) {
    const newAnswers = { ...answers, [currentQuestion.id]: value };
    setAnswers(newAnswers);
    trackEvent("quiz_question_answered", {
      question: currentQuestion.id,
      answer: value,
      step: currentStep + 1,
    });

    if (currentStep < totalSteps - 1) {
      setTimeout(() => setCurrentStep(currentStep + 1), 300);
    } else {
      // Last question - show analyzing state then results
      trackEvent("quiz_completed", { answers: newAnswers });
      setIsAnalyzing(true);
    }
  }

  useEffect(() => {
    if (isAnalyzing) {
      const timer = setTimeout(() => {
        setResults(getResults(answers));
        setIsAnalyzing(false);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [isAnalyzing, answers]);

  function retakeQuiz() {
    trackEvent("quiz_retake_click");
    setCurrentStep(0);
    setAnswers({});
    setResults(null);
    setIsAnalyzing(false);
  }

  // Analyzing State
  if (isAnalyzing) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center text-center px-4">
        <div className="mb-8">
          <Spinner size="lg" />
        </div>
        <h2 className="font-display text-2xl sm:text-3xl text-brand-dark mb-3 animate-fade-in">
          {t("analyzing")}
        </h2>
        <p className="text-sm text-brand-muted animate-fade-in" style={{ animationDelay: "0.3s" }}>
          {t("findingMatches")}
        </p>
      </div>
    );
  }

  // Results State
  if (results) {
    return (
      <div className="container-luxury py-12 sm:py-16">
        <div className="text-center mb-12 animate-fade-up">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Sparkles className="h-6 w-6 text-brand-gold" />
            <p className="text-xs font-medium uppercase tracking-[0.3em] text-brand-gold">
              {t("results")}
            </p>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-brand-dark mb-3">
            {t("yourMatches")}
          </h1>
          <p className="text-sm text-brand-muted">
            {t("basedOnAnswers")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
          {results.map((result, idx) => (
            <div
              key={result.product.id}
              className="border border-gray-100 hover:border-brand-gold/30 transition-all group animate-fade-up"
              style={{ animationDelay: `${idx * 0.15}s` }}
            >
              {/* Image placeholder */}
              <div className="relative aspect-[3/4] bg-brand-cream flex items-center justify-center overflow-hidden">
                <ShoppingBag className="h-12 w-12 text-brand-gold/20" />
                {/* Match badge */}
                <div className="absolute top-3 left-3 bg-brand-gold text-white px-3 py-1.5 text-xs font-bold tracking-wider">
                  {t("match", { percent: result.matchPercent })}
                </div>
              </div>

              <div className="p-6">
                <h3 className="font-display text-xl text-brand-dark mb-1">
                  {result.product.name}
                </h3>
                <p className="text-lg text-brand-gold mb-3">
                  {formatPrice(result.product.price)}
                </p>
                <p className="text-xs text-brand-muted leading-relaxed mb-4">
                  {result.reason}
                </p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {Object.entries(result.product.specs).map(([key, val]) => (
                    <span
                      key={key}
                      className="text-[11px] uppercase tracking-wider text-brand-muted bg-brand-cream px-2 py-1"
                    >
                      {val}
                    </span>
                  ))}
                </div>
                <Button asChild variant="primary" size="md" className="w-full">
                  <Link
                    href={`/wigs/${result.product.slug}`}
                    onClick={() => trackEvent("quiz_result_product_click", { productId: result.product.id, productName: result.product.name, matchPercent: result.matchPercent })}
                  >
                    {tc("shopNow")}
                  </Link>
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <button
            onClick={retakeQuiz}
            className="inline-flex items-center space-x-2 text-sm text-brand-muted hover:text-brand-gold transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            <span>{t("retakeQuiz")}</span>
          </button>
        </div>
      </div>
    );
  }

  // Quiz Questions State
  return (
    <div className="min-h-[80vh] flex flex-col">
      {/* Progress Bar */}
      <div className="w-full bg-gray-100">
        <div
          className="h-1 bg-brand-gold transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        {/* Step counter */}
        <p className="text-xs uppercase tracking-widest text-brand-muted mb-8">
          {t("questionOf", { current: currentStep + 1, total: totalSteps })}
        </p>

        {/* Question */}
        <h2
          className="font-display text-2xl sm:text-3xl lg:text-4xl text-brand-dark text-center mb-10 max-w-2xl animate-fade-in"
          key={currentStep}
        >
          {currentQuestion.question}
        </h2>

        {/* Answer Cards */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-xl animate-fade-up"
          key={`answers-${currentStep}`}
        >
          {currentQuestion.options.map((option) => (
            <button
              key={option.value}
              onClick={() => selectAnswer(option.value)}
              className={cn(
                "group relative p-6 border text-start transition-all duration-300",
                "hover:border-brand-gold hover:shadow-md",
                answers[currentQuestion.id] === option.value
                  ? "border-brand-gold bg-brand-cream/50"
                  : "border-gray-200"
              )}
            >
              <span className="text-sm font-medium text-brand-dark group-hover:text-brand-gold transition-colors">
                {option.label}
              </span>
            </button>
          ))}
        </div>

        {/* Back button */}
        {currentStep > 0 && (
          <button
            onClick={() => setCurrentStep(currentStep - 1)}
            className="mt-10 inline-flex items-center space-x-2 text-sm text-brand-muted hover:text-brand-gold transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{t("previousQuestion")}</span>
          </button>
        )}
      </div>
    </div>
  );
}
